import type {
  WesApiDetailRow,
  WesApiDimension,
  WesApiFilters,
  WesApiReadRequest,
  WesApiSortField,
  WesApiStagingScope,
} from './wesPalletStagingApi';
import type { WesFilterSelection, WesHierarchyLevel, WesTimeGrain } from './useWesPalletStagingFilters';
import { WES_WEEKS_PER_PERIOD } from './wesPalletStagingStaticData';
import type { WesStagingScope } from './wesPalletStagingStaticRows';
import type { WesDimKey, WesDisplayMode, WesTableSort } from './wesPalletStagingTable';

export interface WesRequestSettings {
  scope?: WesStagingScope;
  hierarchy?: WesHierarchyLevel;
  timeGrain?: WesTimeGrain;
  dimensions?: WesDimKey[];
  sort?: WesTableSort | null;
  displayMode?: WesDisplayMode;
  page?: number;
  pageSize?: number;
}

export type WesRequestResult = { ok: true; request: WesApiReadRequest } | { ok: false; reason: string };

const SCOPE: Record<WesStagingScope, WesApiStagingScope> = { all: 'ALL', audit: 'AUDIT_RELATED', other: 'OTHER' };

export const DIMENSION: Record<WesDimKey, WesApiDimension> = {
  region: 'REGION',
  market: 'MARKET',
  location: 'LOCATION',
  locationId: 'LOCATION_ID',
  year: 'YEAR',
  period: 'PERIOD',
  week: 'WEEK',
};

// The backend sorts only the four metric columns; dimension sorts are done on the full export in useWesLocationDetail.
const SORT_FIELD: Record<string, WesApiSortField> = {
  total: 'TOTAL',
  staged: 'STAGED',
  other: 'STAGED_OTHER',
  audit: 'STAGED_AUDIT_RELATED',
};

export const isWesApiSortable = (key: string) => key in SORT_FIELD;

const DIMENSION_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

// The sort when it targets a visible dimension column, which the backend cannot sort.
export const dimensionSortOf = (sort: WesTableSort | null | undefined, dimensions: WesDimKey[]): { key: WesDimKey; dir: 'asc' | 'desc' } | null =>
  sort && sort.key in DIMENSION && dimensions.includes(sort.key as WesDimKey) ? { key: sort.key as WesDimKey, dir: sort.dir } : null;

export const sortRowsByDimension = (rows: WesApiDetailRow[], key: WesDimKey, dir: 'asc' | 'desc'): WesApiDetailRow[] => {
  const field = DIMENSION[key];
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => DIMENSION_COLLATOR.compare(a.dimensions[field] ?? '', b.dimensions[field] ?? '') * factor);
};

const DEFAULT_SORT_FIELD: WesApiSortField = 'TOTAL';

const intOf = (token: string, pattern: RegExp): number | null => {
  const match = pattern.exec(token);
  return match ? Number(match[1]) : null;
};

// The weekInYear of the w-th week of period p, for every fiscal year in the period-weeks data; the 4-weeks-per-period formula when the data has no such period.
export const weeksInYearOf = (periodWeeks: any[], period: number, weekInPeriod: number, years?: number[]): number[] => {
  const found: number[] = [];
  periodWeeks.forEach((yearData: any) => {
    if (years && years.length > 0 && !years.includes(Number(yearData?.year))) return;
    const periodData = (yearData?.periods ?? []).find((pd: any) => Number(pd.period) === period);
    const week = periodData?.weeks?.[weekInPeriod - 1];
    if (week === undefined || week === null) return;
    const value = typeof week === 'number' ? week : Number(week?.weekInYear ?? week?.week);
    if (!Number.isNaN(value)) found.push(value);
  });
  return found.length > 0 ? found : [(period - 1) * WES_WEEKS_PER_PERIOD + weekInPeriod];
};

const unique = (values: number[]) => Array.from(new Set(values)).sort((a, b) => a - b);

// Builds the filter body, or the reason a selection cannot be sent; sections must not request in that case.
export const buildWesFilters = (
  applied: WesFilterSelection,
  periodWeeks: any[],
): { ok: true; filters: WesApiFilters } | { ok: false; reason: string } => {
  const filters: WesApiFilters = {};
  if (applied.regions.length > 0) filters.regions = [...applied.regions];
  if (applied.markets.length > 0) filters.markets = [...applied.markets];
  if (applied.locations.length > 0) filters.locations = [...applied.locations];

  if (applied.years.length > 0) {
    const years = applied.years.map(Number);
    if (years.some((y) => Number.isNaN(y))) return { ok: false, reason: 'Invalid year selection' };
    filters.years = unique(years);
  }

  if (applied.periods.length > 0) {
    const periods = applied.periods.map((token) => intOf(token, /^P(\d+)$/));
    if (periods.some((p) => p === null)) return { ok: false, reason: 'Invalid period selection' };
    filters.periods = unique(periods as number[]);
  }

  if (applied.weeks.length > 0) {
    const weeks: number[] = [];
    for (const token of applied.weeks) {
      const match = /^P(\d+)W(\d+)$/.exec(token);
      if (!match) return { ok: false, reason: 'Invalid week selection' };
      weeks.push(...weeksInYearOf(periodWeeks, Number(match[1]), Number(match[2]), filters.years));
    }
    filters.weeks = unique(weeks);
  }
  return { ok: true, filters };
};

export const buildWesReadRequest = (
  applied: WesFilterSelection,
  periodWeeks: any[],
  settings: WesRequestSettings = {},
): WesRequestResult => {
  const built = buildWesFilters(applied, periodWeeks);
  if (!built.ok) return built;

  const request: WesApiReadRequest = { filters: built.filters };
  if (settings.scope) request.stagingScope = SCOPE[settings.scope];
  if (settings.hierarchy) request.hierarchy = settings.hierarchy.toUpperCase() as WesApiReadRequest['hierarchy'];
  if (settings.timeGrain) request.timeGrain = settings.timeGrain.toUpperCase() as WesApiReadRequest['timeGrain'];
  if (settings.dimensions) request.dimensions = settings.dimensions.map((d) => DIMENSION[d]);
  if (settings.sort !== undefined) {
    const field = settings.sort && SORT_FIELD[settings.sort.key] ? SORT_FIELD[settings.sort.key] : DEFAULT_SORT_FIELD;
    request.sortField = field;
    request.sortDirection = settings.sort && SORT_FIELD[settings.sort.key] ? (settings.sort.dir === 'asc' ? 'ASC' : 'DESC') : 'ASC';
  }
  if (settings.displayMode) request.displayMode = settings.displayMode === 'percent' ? 'PERCENT' : 'PALLETS';
  if (settings.page !== undefined) request.page = settings.page;
  if (settings.pageSize !== undefined) request.size = settings.pageSize;
  return { ok: true, request };
};
